'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslations } from 'next-intl';
import type {
  ChatBranchSubmitPreference,
  PromptOption,
  TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { toasterApi } from '../../toast/ToastProvider';
import {
  extractChatBranchEditSource,
  getAskUserConfirmationAnchorIds,
  resolveAskUserBranchContext,
  type ChatAskUserBranchEditSource,
  type ChatBranchEditSource,
  type ChatComposerBranchEditSource,
} from '../utils/chat-branching';
import type {
  ChatBranchConfirmChoice,
  ChatBranchConfirmVariant,
} from '../components/ChatBranchConfirmationDialog';
import type { InlineUserMessageEditSubmit } from '../components/InlineUserMessageEditor';
import type { ChatInlineEditCallbacks } from '../components/chat-message-list-editing';

export type SendBranchInlineEdit = (args: {
  edit: InlineUserMessageEditSubmit;
  branchFromMessageId: string;
  branchEditSource: ChatComposerBranchEditSource;
}) => Promise<boolean | void>;

export type ChatBranchInlineEdit = Omit<
  ChatInlineEditCallbacks,
  'editorRef' | 'onMentionsTrigger'
>;

export type RewindChatThread = (args: {
  threadId: string;
  fromMessageId: string;
  revertFiles: boolean;
}) => Promise<{
  history: TerraUIMessage[];
  branchedFromMessageId: string;
  removedMessageCount: number;
}>;

export type UseChatMessageBranchingParams = {
  rewindChatThread: RewindChatThread;
  threadId: string;
  setMessages: (
    messages:
      | TerraUIMessage[]
      | ((messages: TerraUIMessage[]) => TerraUIMessage[]),
  ) => void;
  onBranchApplied?: (args: {
    removedMessageCount: number;
    branchedFromMessageId: string;
  }) => void;
  abortInFlightTurn?: () => Promise<void> | void;
  submitPreference?: ChatBranchSubmitPreference;
  onSubmitPreferenceChange?: (preference: ChatBranchSubmitPreference) => void;
  sendInlineEdit?: SendBranchInlineEdit;
};

type CompletedBranchRewind = {
  messageId: string;
  branchedFromMessageId: string;
};

type PendingBranchSend = (args: {
  branchFromMessageId: string;
  branchEditSource: ChatBranchEditSource;
}) => Promise<boolean | void>;

export function useChatMessageBranching({
  rewindChatThread,
  threadId,
  setMessages,
  onBranchApplied,
  abortInFlightTurn,
  submitPreference = 'ask',
  onSubmitPreferenceChange,
  sendInlineEdit,
}: UseChatMessageBranchingParams) {
  const sendInlineEditRef = useRef(sendInlineEdit);

  sendInlineEditRef.current = sendInlineEdit;
  const tBranching = useTranslations('designSystem.chat.branching');
  const [branchEditSource, setBranchEditSource] =
    useState<ChatBranchEditSource | null>(null);
  const [isBranchConfirmOpen, setIsBranchConfirmOpen] = useState(false);
  const [isApplyingBranch, setIsApplyingBranch] = useState(false);
  const isConfirmingBranchRef = useRef(false);
  const branchEditSourceRef = useRef(branchEditSource);

  branchEditSourceRef.current = branchEditSource;
  const pendingBranchSendRef = useRef<PendingBranchSend | null>(null);
  const [completedRewind, setCompletedRewindState] =
    useState<CompletedBranchRewind | null>(null);
  const completedRewindRef = useRef(completedRewind);

  const setCompletedRewind = useCallback(
    (rewind: CompletedBranchRewind | null) => {
      completedRewindRef.current = rewind;
      setCompletedRewindState(rewind);
    },
    [],
  );

  const clearBranchEdit = useCallback(() => {
    setBranchEditSource(null);
    pendingBranchSendRef.current = null;
    setCompletedRewind(null);
  }, [setCompletedRewind]);

  const handleEditAskUserAnswer = useCallback(
    ({
      messages,
      toolCallId,
      toolCallPartId,
    }: {
      messages: TerraUIMessage[];
      toolCallId?: string;
      toolCallPartId?: string;
    }) => {
      if (isApplyingBranch) {
        return;
      }

      const context = resolveAskUserBranchContext({
        messages,
        toolCallId,
        toolCallPartId,
      });

      if (!context) {
        toasterApi.error({
          title: tBranching('changeAnswerFailedTitle'),
          description: tBranching('changeAnswerFailedDescription'),
        });

        return;
      }

      setBranchEditSource({
        kind: 'ask-user',
        messageId: context.userMessageId,
        message: context.userMessage,
        askUserDataMessage: context.askUserDataMessage,
      });
    },
    [isApplyingBranch, tBranching],
  );

  const handleEditUserMessage = useCallback(
    (messageId: string, messages: TerraUIMessage[]) => {
      if (isApplyingBranch) {
        return;
      }

      const message = messages.find((entry) => entry.id === messageId);

      if (!message) {
        return;
      }

      const source = extractChatBranchEditSource({ message });

      if (!source) {
        const askUserAnchorIds = getAskUserConfirmationAnchorIds({ message });

        if (askUserAnchorIds.length > 0) {
          handleEditAskUserAnswer({
            messages,
            toolCallPartId: askUserAnchorIds[0],
            toolCallId: askUserAnchorIds[1],
          });
        }

        return;
      }

      setBranchEditSource(source);
    },
    [handleEditAskUserAnswer, isApplyingBranch],
  );

  const cancelInlineEdit = useCallback(() => {
    if (isConfirmingBranchRef.current || isApplyingBranch) {
      return;
    }

    if (branchEditSourceRef.current?.kind === 'composer') {
      clearBranchEdit();
    }
  }, [clearBranchEdit, isApplyingBranch]);

  const confirmBranchResend = useCallback(
    async ({ revertFiles, rememberChoice }: ChatBranchConfirmChoice) => {
      const pendingSend = pendingBranchSendRef.current;
      const activeBranchEditSource = branchEditSourceRef.current;

      if (!activeBranchEditSource || !pendingSend) {
        toasterApi.error({
          title: tBranching('confirmFailedTitle'),
          description: tBranching('confirmFailedDescription'),
        });
        setIsBranchConfirmOpen(false);

        return;
      }

      const confirmedBranchEditMessageId = activeBranchEditSource.messageId;
      const completedRewind = completedRewindRef.current;
      const isRetryingSend =
        completedRewind?.messageId === confirmedBranchEditMessageId;

      if (rememberChoice && !isRetryingSend) {
        onSubmitPreferenceChange?.(revertFiles ? 'revert' : 'keep');
      }

      isConfirmingBranchRef.current = true;
      pendingBranchSendRef.current = null;
      setIsApplyingBranch(true);
      const reopenAfterFailure = () => {
        pendingBranchSendRef.current = pendingSend;
        setIsBranchConfirmOpen(true);
        toasterApi.error({
          title: tBranching('confirmFailedTitle'),
          description: tBranching('confirmFailedDescription'),
        });
      };

      const applyRewind = async (): Promise<string> => {
        await abortInFlightTurn?.();

        const rewindResult = await rewindChatThread({
          threadId,
          fromMessageId: confirmedBranchEditMessageId,
          revertFiles,
        });

        flushSync(() => {
          setMessages(rewindResult.history);
        });
        onBranchApplied?.({
          removedMessageCount: rewindResult.removedMessageCount,
          branchedFromMessageId: rewindResult.branchedFromMessageId,
        });
        setCompletedRewind({
          messageId: confirmedBranchEditMessageId,
          branchedFromMessageId: rewindResult.branchedFromMessageId,
        });

        return rewindResult.branchedFromMessageId;
      };

      try {
        const branchFromMessageId = isRetryingSend
          ? completedRewind.branchedFromMessageId
          : await applyRewind();

        setIsBranchConfirmOpen(false);

        void pendingSend({
          branchFromMessageId,
          branchEditSource: activeBranchEditSource,
        })
          .then((sendSucceeded) => {
            if (sendSucceeded === false) {
              reopenAfterFailure();

              return;
            }

            setCompletedRewind(null);

            if (
              branchEditSourceRef.current?.messageId ===
              confirmedBranchEditMessageId
            ) {
              setBranchEditSource(null);
            }
          })
          .catch((error: unknown) => {
            console.warn(
              '[useChatMessageBranching] branch resend failed after rewind',
              error,
            );
            reopenAfterFailure();
          })
          .finally(() => {
            setIsApplyingBranch(false);
          });
      } catch (error: unknown) {
        console.warn('Branch rewind failed', error);
        setIsApplyingBranch(false);
        reopenAfterFailure();
      } finally {
        isConfirmingBranchRef.current = false;
      }
    },
    [
      abortInFlightTurn,
      onBranchApplied,
      onSubmitPreferenceChange,
      rewindChatThread,
      setCompletedRewind,
      setMessages,
      tBranching,
      threadId,
    ],
  );

  const submitWithPreference = useCallback(
    (pendingSend: PendingBranchSend) => {
      pendingBranchSendRef.current = pendingSend;

      if (submitPreference === 'ask') {
        setIsBranchConfirmOpen(true);

        return;
      }

      void confirmBranchResend({
        revertFiles: submitPreference === 'revert',
        rememberChoice: false,
      });
    },
    [confirmBranchResend, submitPreference],
  );

  const requestInlineEditSubmit = useCallback(
    (
      sendAfterBranch: (args: {
        branchFromMessageId: string;
        branchEditSource: ChatComposerBranchEditSource;
      }) => Promise<boolean | void>,
    ): boolean => {
      if (
        isApplyingBranch ||
        branchEditSourceRef.current?.kind !== 'composer'
      ) {
        return false;
      }

      submitWithPreference(
        async ({ branchFromMessageId, branchEditSource: activeSource }) =>
          activeSource.kind === 'composer'
            ? sendAfterBranch({
                branchFromMessageId,
                branchEditSource: activeSource,
              })
            : false,
      );

      return true;
    },
    [isApplyingBranch, submitWithPreference],
  );

  const requestBranchAskUserResend = useCallback(
    async (
      sendAfterBranch: (args: {
        branchFromMessageId: string;
        branchEditSource: ChatAskUserBranchEditSource;
        selectedOption: PromptOption;
      }) => Promise<boolean | void>,
      selectedOption: PromptOption,
    ) => {
      if (branchEditSourceRef.current?.kind !== 'ask-user') {
        return true;
      }

      submitWithPreference(
        async ({ branchFromMessageId, branchEditSource: activeSource }) =>
          activeSource.kind === 'ask-user'
            ? sendAfterBranch({
                branchFromMessageId,
                branchEditSource: activeSource,
                selectedOption,
              })
            : false,
      );

      return false;
    },
    [submitWithPreference],
  );

  const cancelBranchResend = useCallback(() => {
    if (isConfirmingBranchRef.current) {
      return;
    }

    setIsBranchConfirmOpen(false);
    pendingBranchSendRef.current = null;
    setCompletedRewind(null);

    if (branchEditSourceRef.current?.kind === 'ask-user') {
      setBranchEditSource(null);
    }
  }, [setCompletedRewind]);

  const handleBranchConfirmOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (open) {
        setIsBranchConfirmOpen(true);

        return;
      }

      if (isConfirmingBranchRef.current) {
        setIsBranchConfirmOpen(false);

        return;
      }

      cancelBranchResend();
    },
    [cancelBranchResend],
  );

  const editingMessageId =
    branchEditSource?.kind === 'composer'
      ? branchEditSource.messageId
      : undefined;

  const inlineEdit = useMemo<ChatBranchInlineEdit | undefined>(() => {
    if (!editingMessageId) {
      return undefined;
    }

    return {
      editingMessageId,
      onSubmit: (edit) => {
        requestInlineEditSubmit(async (args) =>
          sendInlineEditRef.current
            ? sendInlineEditRef.current({ edit, ...args })
            : false,
        );
      },
      onCancel: cancelInlineEdit,
      isSubmitting: isApplyingBranch,
      isCancelBlocked: isBranchConfirmOpen || isApplyingBranch,
    };
  }, [
    cancelInlineEdit,
    editingMessageId,
    isApplyingBranch,
    isBranchConfirmOpen,
    requestInlineEditSubmit,
  ]);

  const branchConfirmVariant: ChatBranchConfirmVariant =
    completedRewind && completedRewind.messageId === branchEditSource?.messageId
      ? 'retry-send'
      : branchEditSource?.kind === 'ask-user'
        ? 'ask-user'
        : 'composer';

  return {
    branchEditSource,
    editingMessageId,
    inlineEdit,
    branchConfirmVariant,
    isBranchConfirmOpen,
    isApplyingBranch,
    handleEditUserMessage,
    handleEditAskUserAnswer,
    requestInlineEditSubmit,
    requestBranchAskUserResend,
    confirmBranchResend,
    cancelBranchResend,
    cancelInlineEdit,
    handleBranchConfirmOpenChange,
    clearBranchEdit,
  };
}
